import { createContext, useContext, useState, useCallback } from 'react';
import fr from './fr.json';
import en from './en.json';

const translations = { fr, en };
const STORAGE_KEY = 'nexus-lang';

const LanguageContext = createContext(null);

/**
 * Navigate a nested object by dot-separated key path.
 * e.g. get(obj, 'landing.hero.title') → obj.landing.hero.title
 * Supports {var} template replacement via params object.
 */
function get(obj, path, params) {
  const val = path.split('.').reduce((acc, part) => acc?.[part], obj);
  if (val == null) return null;
  if (typeof val !== 'string') return val;
  if (!params) return val;
  return Object.entries(params).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), v),
    val,
  );
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'fr';
    } catch {
      return 'fr';
    }
  });

  const setLang = useCallback((newLang) => {
    setLangState(newLang);
    try {
      localStorage.setItem(STORAGE_KEY, newLang);
    } catch {
      // localStorage unavailable
    }
  }, []);

  /**
   * Translate a key. Falls back to FR if key missing in current language.
   * Usage: t('landing.hero.title') or t('register.step2.selected_count', { count: 3 })
   */
  const t = useCallback(
    (key, params) => {
      const result = get(translations[lang], key, params);
      if (result != null) return result;
      // Fallback to FR
      const fallback = get(translations.fr, key, params);
      if (fallback != null) return fallback;
      // Key not found — return the key itself for debugging
      return key;
    },
    [lang],
  );

  return (
    <LanguageContext.Provider value={{ t, lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return ctx;
}

export default useTranslation;
